import React, { useEffect, useState, useContext } from "react";
import { initSchedule } from "../../../utils/data/initData";
import { AuthContext } from "../../../contexts/AuthContext";
import { useErrorDialog } from "../../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../../contexts/SuccessDialogContext";
import ComponentLoading from "../../../components/App/ComponentLoading";
import { handleScheduleDeletion } from "../utils/handleScheduleDeletion";
import FtcSchedule from "../../../components/Schedules/ForeignTaxCredit/FtcSchedule";
import { useAccessControl } from "../../../utils/hooks/useAccessControl";
import { FOREIGN_INCOME_TRACKER_SCHEDULES } from "../../../utils/data/controlData";

const ForeignTaxCredit = ({ company, scheduleId }) => {
  const { updatePLSchedule, getScheduleData, deleteScheduleByScheduleType } =
    useContext(AuthContext);

  const { canEdit: canEditForeignTaxCreditSchedule } = useAccessControl(
    FOREIGN_INCOME_TRACKER_SCHEDULES
  );

  const [saveTrigger, setSaveTrigger] = useState(false);
  const [schedule, setSchedule] = useState({});
  const [scheduleDetails, setScheduleDetails] = useState({});

  const [loading, setLoading] = useState(true);

  const handleError = useErrorDialog();
  const handleSuccess = useSuccessDialog();

  const saveSchedule = async (schedule, scheduleDetails) => {
    if (!company || !scheduleId) {
      handleError("Unexpected error happens. Please try again later.");
      return;
    }

    const updatedSchedule = {
      sch_id: scheduleId,
      scheduleType: "Foreign_Tax_Credit",
      scheduleTitle: schedule.name,
      taxAdjustment: schedule.taxAdjustment,
      totalAdjustment: scheduleDetails.totalAdjustment,
      breakdownRows: scheduleDetails.breakdownRows,
      rows: scheduleDetails.dataRows,
      doc: scheduleDetails.doc,
      notes: schedule.notes,
      reviewNotes: schedule.reviewNotes,
    };

    setLoading(true);
    const response = await updatePLSchedule(updatedSchedule);
    setLoading(false);

    if (response) {
      handleSuccess("The schedule has been saved successfully.");
      setSaveTrigger((prev) => !prev);
    } else {
      handleError(
        "There was a problem saving the schedule. Please contact support if the issue persists."
      );
    }
  };

  const deleteSchedule = async () => {
    await handleScheduleDeletion({
      scheduleId,
      deleteFn: deleteScheduleByScheduleType,
      handleSuccess,
      handleError,
      setLoading,
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      if (!company || !scheduleId) {
        handleError(
          "We encountered an issue while fetching the data. Please refresh the page or try again shortly."
        );
        return;
      }
      //API call here
      const response = await getScheduleData(scheduleId, "Foreign_Tax_Credit");

      const scheduleData = response.schedule;

      //init
      if (scheduleData) {
        setSchedule(initSchedule(scheduleData, "Foreign Tax Credit"));

        setScheduleDetails({
          breakdownRows: scheduleData.breakdownRows || {
            totalRevenue: 0,
            adjustedProfitLoss: 0,
            taxPayable: 0,
          },
          dataRows: scheduleData.rows,
        });
      }
      setLoading(false);
    };
    fetchData();
  }, [saveTrigger, scheduleId, getScheduleData, company.uen, updatePLSchedule]);

  return (
    <>
      {loading ? (
        <ComponentLoading />
      ) : (
        <FtcSchedule
          company={company}
          schedule={schedule}
          scheduleDetails={scheduleDetails}
          saveSchedule={saveSchedule}
          deleteSchedule={deleteSchedule}
          canEditSchedule={canEditForeignTaxCreditSchedule}
        />
      )}
    </>
  );
};

export default ForeignTaxCredit;
